import {
  attachmentListSchema,
  attachmentSchema,
  CSRF_HEADER,
  type DocumentOrigin,
  type UploadAttachmentRequest,
  uploadAttachmentResponseSchema,
} from "@esi/shared";
import { api } from "../../api.ts";
import { ApiError, noContent, ok } from "../../api-errors.ts";

/** API-only metadata; upload targets never enter a query cache. */
export async function listAttachments(conversationId: string, signal?: AbortSignal) {
  const raw = await ok(
    await api.api.conversations[":conversationId"].attachments.$get(
      {
        param: { conversationId },
      },
      { init: { signal } },
    ),
  );
  const parsed = attachmentListSchema.safeParse(raw);
  if (!parsed.success) {
    throw new ApiError(502, "Attachment metadata unavailable", "attachment_metadata");
  }
  const list = parsed.data;
  if (list.composer) {
    const ids = new Set(list.attachments.map((a) => a.id));
    const states = list.composer.attachmentStates;
    // Reject mismatched/duplicate state and defaults; absence of a state is unknown at send.
    if (
      states.some((s) => !ids.has(s.attachmentId)) ||
      new Set(states.map((s) => s.attachmentId)).size !== states.length ||
      !list.composer.uploadPolicy.classifications.some(
        (l) => l.id === list.composer?.uploadPolicy.defaultClassification,
      )
    ) {
      throw new ApiError(502, "Attachment metadata unavailable", "attachment_metadata");
    }
  }
  return list;
}

export function attachmentApi(conversationId: string, csrfToken: string) {
  const headers = { [CSRF_HEADER]: csrfToken };
  const route = api.api.conversations[":conversationId"].attachments;
  const param = (attachmentId: string) => ({ conversationId, attachmentId });
  return {
    async intent(json: UploadAttachmentRequest) {
      return uploadAttachmentResponseSchema.parse(
        await ok(await route.$post({ param: { conversationId }, json }, { headers })),
      );
    },
    async get(attachmentId: string) {
      return attachmentSchema.parse(
        await ok(
          await route[":attachmentId"].$get({
            param: param(attachmentId),
          }),
        ),
      );
    },
    async confirm(attachmentId: string) {
      return attachmentSchema.parse(
        await ok(
          await route[":attachmentId"].confirm.$post({ param: param(attachmentId) }, { headers }),
        ),
      );
    },
    async retry(attachmentId: string) {
      return attachmentSchema.parse(
        await ok(
          await route[":attachmentId"].retry.$post({ param: param(attachmentId) }, { headers }),
        ),
      );
    },
    /** A standalone file's origin, until a message first uses it (409 `attachment_sent`). */
    async origin(attachmentId: string, origin: DocumentOrigin) {
      return attachmentSchema.parse(
        await ok(
          await route[":attachmentId"].$patch(
            { param: param(attachmentId), json: { origin } },
            { headers },
          ),
        ),
      );
    },
    async delete(attachmentId: string) {
      return noContent(
        await route[":attachmentId"].$delete({ param: param(attachmentId) }, { headers }),
      );
    },
  };
}

/** Per-click same-origin authorization redirect, never its signed target. */
export const attachmentDownloadHref = (conversationId: string, attachmentId: string) =>
  `/api/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/download`;

/** The same redirect to an inline URL (PDF, PNG, JPEG), opened in a new tab. */
export const attachmentOpenHref = (conversationId: string, attachmentId: string) =>
  `/api/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/open`;
